import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "@/state/hooks";
import { requestProductContextMeshAnalysis } from "../products/api";
import { refreshContextMeshAnalysis } from "../contextMesh/contextMeshApi";
import {
    classifyContextMeshProvenance,
    ContextMeshApiError,
    fetchContextMeshAnalysis,
    fetchWorkspaceContextMeshAnalysis,
    type ContextMeshAnalysis,
    type ContextMeshAnalysisResponse
} from "./contextMeshAnalysis";

export type MeshStrengthStatus = "ready" | "stale" | "analyzing" | "unavailable";

export interface MeshStrengthData {
    analysis: ContextMeshAnalysis | null;
    status: MeshStrengthStatus;
    loading: boolean;
    error: ContextMeshApiError | null;
    retry: () => void;
}

export interface ContextMeshRefreshNotice {
    kind: "success" | "error";
    message: string;
}

export interface ContextMeshScoreRefresh {
    refreshing: boolean;
    refreshNotice: ContextMeshRefreshNotice | null;
    refreshScore: () => Promise<boolean>;
}

const FAST_ANALYSIS_POLL_INTERVAL_MS = 2_000;
const SLOW_ANALYSIS_POLL_INTERVAL_MS = 10_000;
const FAST_ANALYSIS_POLL_ATTEMPTS = 15;
// A link/assignment write can commit before the background analyzer records a
// pending snapshot. After an explicit return from Files or Integrations, keep
// checking briefly even if the first response still looks current.
const RETURN_REFRESH_POLL_ATTEMPTS = 15;
// Share only active network requests. Resolved analysis must not survive a
// remount because the user may have changed this product on another surface.
const analysisRequests = new Map<string, Promise<ContextMeshAnalysisResponse>>();

export function __clearMeshStrengthCache(): void {
    analysisRequests.clear();
}

function cacheKey(userId: number, projectId: number): string {
    return `${userId}:${projectId}`;
}

function getAnalysis(token: string, userId: number, projectId: number): Promise<ContextMeshAnalysisResponse> {
    const key = cacheKey(userId, projectId);
    const existing = analysisRequests.get(key);
    if (existing) return existing;
    const request = fetchContextMeshAnalysis(token, projectId).finally(() => {
        if (analysisRequests.get(key) === request) analysisRequests.delete(key);
    });
    analysisRequests.set(key, request);
    return request;
}

function getWorkspaceAnalysis(token: string, userId: number, contextMeshId: string): Promise<ContextMeshAnalysisResponse> {
    const key = `${userId}:workspace:${contextMeshId}`;
    const existing = analysisRequests.get(key);
    if (existing) return existing;
    const request = fetchWorkspaceContextMeshAnalysis(token, contextMeshId).finally(() => {
        if (analysisRequests.get(key) === request) analysisRequests.delete(key);
    });
    analysisRequests.set(key, request);
    return request;
}

export function useMeshStrengthData(projectId: number | null, refreshSignal = 0): MeshStrengthData {
    const token = useAppSelector(state => state.clientToken.token);
    const userId = useAppSelector(state => state.userProfiles.userProfile?.userid);
    const [analysis, setAnalysis] = useState<ContextMeshAnalysis | null>(null);
    const [status, setStatus] = useState<MeshStrengthStatus>("unavailable");
    const [loading, setLoading] = useState(projectId != null);
    const [error, setError] = useState<ContextMeshApiError | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (projectId == null) {
            setAnalysis(null);
            setStatus("unavailable");
            setError(null);
            setLoading(false);
            return;
        }
        setAnalysis(current => (current?.productId === projectId ? current : null));
        if (!token || userId == null) {
            setLoading(true);
            return;
        }
        let cancelled = false;
        let pollTimer: ReturnType<typeof setTimeout> | null = null;
        let pollAttempts = 0;
        let returnPollAttemptsRemaining = refreshSignal > 0 ? RETURN_REFRESH_POLL_ATTEMPTS : 0;
        let observedPendingAnalysis = false;
        setLoading(true);
        setError(null);

        const load = (): void => {
            getAnalysis(token, userId, projectId)
                .then(result => {
                    if (cancelled) return;
                    const classified = classifyContextMeshProvenance(result);
                    setAnalysis(classified.analysis);
                    setStatus(
                        classified.state === "current_canonical"
                            ? "ready"
                            : classified.state === "prior_canonical"
                              ? "stale"
                              : classified.state
                    );
                    setLoading(false);
                    const semanticShouldPoll = result.semanticAnalysis.status === "pending";
                    const categoryShouldPoll = classified.analysis?.categoryAnalysis?.status === "pending";
                    const shouldPoll = semanticShouldPoll || categoryShouldPoll;
                    if (shouldPoll) observedPendingAnalysis = true;
                    const shouldFollowUpCommittedChange = !observedPendingAnalysis && returnPollAttemptsRemaining > 0;
                    if (shouldFollowUpCommittedChange) returnPollAttemptsRemaining -= 1;
                    if (shouldPoll || shouldFollowUpCommittedChange) {
                        const interval = pollAttempts < FAST_ANALYSIS_POLL_ATTEMPTS
                            ? FAST_ANALYSIS_POLL_INTERVAL_MS
                            : SLOW_ANALYSIS_POLL_INTERVAL_MS;
                        pollAttempts += 1;
                        pollTimer = setTimeout(load, interval);
                    }
                })
                .catch(reason => {
                    if (cancelled) return;
                    setAnalysis(null);
                    setStatus("unavailable");
                    setError(
                        reason instanceof ContextMeshApiError
                            ? reason
                            : new ContextMeshApiError(503, { code: "ANALYSIS_UNAVAILABLE", message: "Context analysis is temporarily unavailable." })
                    );
                    setLoading(false);
                });
        };

        load();
        return () => {
            cancelled = true;
            if (pollTimer) clearTimeout(pollTimer);
        };
    }, [projectId, token, userId, attempt, refreshSignal]);

    const retry = useCallback(() => {
        if (projectId != null && userId != null) analysisRequests.delete(cacheKey(userId, projectId));
        setAttempt(value => value + 1);
    }, [projectId, userId]);

    return { analysis, status, loading, error, retry };
}

export function useWorkspaceMeshStrengthData(contextMeshId: string | null, refreshSignal = 0): MeshStrengthData {
    const token = useAppSelector(state => state.clientToken.token);
    const userId = useAppSelector(state => state.userProfiles.userProfile?.userid);
    const [analysis, setAnalysis] = useState<ContextMeshAnalysis | null>(null);
    const [status, setStatus] = useState<MeshStrengthStatus>("unavailable");
    const [loading, setLoading] = useState(contextMeshId != null);
    const [error, setError] = useState<ContextMeshApiError | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (contextMeshId == null) {
            setAnalysis(null);
            setStatus("unavailable");
            setError(null);
            setLoading(false);
            return;
        }
        setAnalysis(null);
        if (!token || userId == null) {
            setLoading(true);
            return;
        }
        let cancelled = false;
        let pollTimer: ReturnType<typeof setTimeout> | null = null;
        let pollAttempts = 0;
        let returnPollAttemptsRemaining = refreshSignal > 0 ? RETURN_REFRESH_POLL_ATTEMPTS : 0;
        let observedPendingAnalysis = false;
        setLoading(true);
        setError(null);

        const load = (): void => {
            getWorkspaceAnalysis(token, userId, contextMeshId)
                .then(result => {
                    if (cancelled) return;
                    const classified = classifyContextMeshProvenance(result);
                    setAnalysis(classified.analysis);
                    setStatus(
                        classified.state === "current_canonical"
                            ? "ready"
                            : classified.state === "prior_canonical"
                              ? "stale"
                              : classified.state
                    );
                    setLoading(false);
                    const semanticShouldPoll = result.semanticAnalysis.status === "pending";
                    const categoryShouldPoll = classified.analysis?.categoryAnalysis?.status === "pending";
                    const shouldPoll = semanticShouldPoll || categoryShouldPoll;
                    if (shouldPoll) observedPendingAnalysis = true;
                    const shouldFollowUpCommittedChange = !observedPendingAnalysis && returnPollAttemptsRemaining > 0;
                    if (shouldFollowUpCommittedChange) returnPollAttemptsRemaining -= 1;
                    if (shouldPoll || shouldFollowUpCommittedChange) {
                        const interval = pollAttempts < FAST_ANALYSIS_POLL_ATTEMPTS
                            ? FAST_ANALYSIS_POLL_INTERVAL_MS
                            : SLOW_ANALYSIS_POLL_INTERVAL_MS;
                        pollAttempts += 1;
                        pollTimer = setTimeout(load, interval);
                    }
                })
                .catch(reason => {
                    if (cancelled) return;
                    setAnalysis(null);
                    setStatus("unavailable");
                    setError(
                        reason instanceof ContextMeshApiError
                            ? reason
                            : new ContextMeshApiError(503, { code: "ANALYSIS_UNAVAILABLE", message: "Context analysis is temporarily unavailable." })
                    );
                    setLoading(false);
                });
        };

        load();
        return () => {
            cancelled = true;
            if (pollTimer) clearTimeout(pollTimer);
        };
    }, [contextMeshId, token, userId, attempt, refreshSignal]);

    const retry = useCallback(() => {
        if (contextMeshId != null && userId != null) {
            analysisRequests.delete(`${userId}:workspace:${contextMeshId}`);
        }
        setAttempt(value => value + 1);
    }, [contextMeshId, userId]);

    return { analysis, status, loading, error, retry };
}

export function useContextMeshScoreRefresh(
    projectId: number | null,
    reloadAnalysis: () => void
): ContextMeshScoreRefresh {
    const token = useAppSelector(state => state.clientToken.token);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshNotice, setRefreshNotice] = useState<ContextMeshRefreshNotice | null>(null);
    const inFlightRef = useRef<Promise<boolean> | null>(null);

    useEffect(() => {
        setRefreshing(false);
        setRefreshNotice(null);
        inFlightRef.current = null;
    }, [projectId]);

    const refreshScore = useCallback((): Promise<boolean> => {
        if (inFlightRef.current) return inFlightRef.current;
        if (!token || projectId == null) {
            setRefreshNotice({ kind: "error", message: "Authentication is required to refresh this score." });
            return Promise.resolve(false);
        }

        setRefreshing(true);
        setRefreshNotice(null);
        const request = requestProductContextMeshAnalysis(token, String(projectId))
            .then(() => {
                reloadAnalysis();
                setRefreshNotice({ kind: "success", message: "Score refreshed." });
                return true;
            })
            .catch(reason => {
                setRefreshNotice({
                    kind: "error",
                    message: reason instanceof Error
                        ? reason.message
                        : "Context analysis could not be refreshed. Try again shortly."
                });
                return false;
            })
            .finally(() => {
                setRefreshing(false);
                if (inFlightRef.current === request) inFlightRef.current = null;
            });
        inFlightRef.current = request;
        return request;
    }, [projectId, reloadAnalysis, token]);

    return { refreshing, refreshNotice, refreshScore };
}

export function useWorkspaceContextMeshScoreRefresh(
    contextMeshId: string | null,
    reloadAnalysis: () => void
): ContextMeshScoreRefresh {
    const token = useAppSelector(state => state.clientToken.token);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshNotice, setRefreshNotice] = useState<ContextMeshRefreshNotice | null>(null);
    const inFlightRef = useRef<Promise<boolean> | null>(null);

    useEffect(() => {
        setRefreshing(false);
        setRefreshNotice(null);
        inFlightRef.current = null;
    }, [contextMeshId]);

    const refreshScore = useCallback((): Promise<boolean> => {
        if (inFlightRef.current) return inFlightRef.current;
        if (!token || contextMeshId == null) {
            setRefreshNotice({ kind: "error", message: "Authentication is required to refresh this score." });
            return Promise.resolve(false);
        }
        setRefreshing(true);
        setRefreshNotice(null);
        const request = refreshContextMeshAnalysis(token, contextMeshId)
            .then(() => {
                reloadAnalysis();
                setRefreshNotice({ kind: "success", message: "Score refreshed." });
                return true;
            })
            .catch(reason => {
                setRefreshNotice({
                    kind: "error",
                    message: reason instanceof Error
                        ? reason.message
                        : "Context analysis could not be refreshed. Try again shortly."
                });
                return false;
            })
            .finally(() => {
                setRefreshing(false);
                if (inFlightRef.current === request) inFlightRef.current = null;
            });
        inFlightRef.current = request;
        return request;
    }, [contextMeshId, reloadAnalysis, token]);

    return { refreshing, refreshNotice, refreshScore };
}
